import React from 'react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Info, ShieldCheck } from 'lucide-react';
import { Button, ConfirmDialog, showToast } from '@ideoz/ui';
import {
  defaultPermissions,
  enabledAreaCount,
  getIntegration,
  type PermLevel,
} from './data/catalog';
import { useIntegrations } from './state/useIntegrations';
import { StatusBadge } from './components/StatusBadge';
import { PermissionAreaCard } from './components/PermissionAreaCard';

/**
 * Integration detail / permissions page.
 *
 * Connect and disconnect are **real** (OAuth via the broker + backend). The
 * per-area permission controls are a **mockup** — granting/saving them is
 * deferred, so the draft lives only in local state and "Save permissions" is a
 * placeholder. Connect / Disconnect use the transparent (outline) button style.
 */
export function IntegrationDetailModule(): JSX.Element {
  const navigate = useNavigate();
  const { startupSlug, toolId } = useParams<{ startupSlug: string; toolId: string }>();
  const { getConnection, isConnected, connect, disconnect } = useIntegrations();

  const integration = getIntegration(toolId);
  const backToList = () => navigate(`/${startupSlug}/integrations`);

  // Editable permission draft (mockup only — seeded from catalog defaults).
  const [draft, setDraft] = useState<Record<string, PermLevel>>(() =>
    integration ? defaultPermissions(integration) : {}
  );
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [busy, setBusy] = useState(false);

  const enabled = useMemo(
    () => (integration ? enabledAreaCount(integration, draft) : 0),
    [integration, draft]
  );

  if (!integration) {
    return (
      <section className="mx-auto w-full max-w-3xl px-6 py-16 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">Integration not found.</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={backToList}>
          Back to integrations
        </Button>
      </section>
    );
  }

  const connection = getConnection(integration.id);
  const connected = isConnected(integration.id);
  const Icon = integration.icon;
  const setArea = (areaId: string, level: PermLevel) =>
    setDraft((prev) => ({ ...prev, [areaId]: level }));

  const handleConnect = async () => {
    setBusy(true);
    try {
      // Same-tab navigation to the broker consent flow — we don't return here.
      await connect(integration.id);
    } catch {
      showToast({ message: `Couldn't start connecting ${integration.name}`, severity: 'error' });
      setBusy(false);
    }
  };

  // Mockup: permissions aren't wired to the backend yet.
  const handleSave = () => {
    showToast({ message: 'Permissions saved', severity: 'success' });
    backToList();
  };

  const handleDisconnect = async () => {
    if (!connection) return;
    setBusy(true);
    try {
      await disconnect(connection.id);
      showToast({ message: `${integration.name} disconnected`, severity: 'success' });
      backToList();
    } catch {
      showToast({ message: `Couldn't disconnect ${integration.name}`, severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto w-full max-w-3xl px-6 py-10">
      {/* Header */}
      <div className="flex items-start gap-4">
        <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          <Icon size={26} />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
              {integration.name}
            </h1>
            {connected && <StatusBadge connected />}
          </div>
          <p className="mt-1 max-w-xl text-sm text-gray-500 dark:text-gray-400">
            {integration.description}
          </p>
        </div>
      </div>

      {/* Permissions (mockup) */}
      <div className="mt-8 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Permissions</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {enabled} of {integration.areas.length} areas enabled
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <ShieldCheck size={13} />
          You can change these at any time
        </span>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        {integration.areas.map((area) => (
          <PermissionAreaCard
            key={area.id}
            area={area}
            value={draft[area.id] ?? area.defaultLevel}
            onChange={(level) => setArea(area.id, level)}
          />
        ))}
      </div>

      <p className="mt-4 flex items-start gap-2 text-xs text-gray-400 dark:text-gray-500">
        <Info size={14} className="mt-0.5 flex-shrink-0" />
        Ideoz only uses these permissions inside the scopes you run. Nothing is shared outside your
        workspace.
      </p>

      <hr className="my-6 border-gray-200 dark:border-gray-700" />

      {/* Actions */}
      <div className="flex items-center gap-3">
        {connected ? (
          <>
            <Button variant="primary" onClick={handleSave} data-testid="integration-save">
              Save permissions
            </Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmDisconnect(true)}
              disabled={busy}
              size="xs"
              data-testid="integration-disconnect"
            >
              Disconnect
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            onClick={() => void handleConnect()}
            disabled={busy}
            size="xs"
            data-testid="integration-connect"
          >
            {integration.connectLabel}
          </Button>
        )}
        <Button variant="ghost" onClick={backToList} data-testid="integration-cancel">
          Cancel
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDisconnect}
        variant="danger"
        title={`Disconnect ${integration.name}?`}
        description={`Ideoz will lose access to ${integration.name}. You can reconnect at any time.`}
        confirmLabel="Disconnect"
        cancelLabel="Cancel"
        onConfirm={() => {
          setConfirmDisconnect(false);
          void handleDisconnect();
        }}
        onCancel={() => setConfirmDisconnect(false)}
      />
    </section>
  );
}
